/**
 * Serialize a primary-key map into a stable index key for the Table Explorer
 * grid's pending-change index (`pendingByPk` in `TableExplorerGrid`).
 *
 * Semantics vs the `primaryKeysMatch` (`===`-based) comparator it replaced:
 * - sorted [key, value] entries → key order never matters;
 * - JSON keeps `1` and `'1'` distinct;
 * - BigInt is serialized as `"<digits>n"` — `JSON.stringify` would THROW on a
 *   raw bigint, which would crash the grid render on the first pending change
 *   against a bigint PK; the `n` suffix keeps `1n` distinct from `1`/`'1'`;
 * - Dates match STRUCTURALLY (via `toJSON`) rather than by object identity —
 *   deliberately laxer than `===` so a pending wash survives a refetch that
 *   rebuilds row objects.
 *
 * Known collisions (both err toward matching, acceptable for pending-state
 * lookup): `undefined` and `null` both serialize to `null`; a `Date` collides
 * with its own ISO string.
 */
export function serializePrimaryKey(pk: Record<string, unknown>): string {
  return JSON.stringify(
    Object.keys(pk).sort().map((k) => [k, pk[k]]),
    (_key, value: unknown) => (typeof value === 'bigint' ? `${value.toString()}n` : value),
  );
}
